import { Page } from "../../../components/page-base";

import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { NameCell } from "../../../components/datagrid/NameCell";
import { StatusChip } from "../../../components/datagrid/StatusChip";
import { KeywordBarCell } from "../../../components/datagrid/KeywordBarCell";
import { iniciais } from "../../../components/datagrid/iniciais";
import {
  AutomationToggle,
  ChipButton,
  ChipGroupLabel,
  TonalButton,
  ToolbarDivider,
} from "../../../components/datagrid/ChipButton";
import { KpiStrip } from "../../../components/kpi-strip";
import { formatarNumero } from "../../../components/kpi-strip/formatar";
import { adminOverviewService } from "../../profile-dashboard/services";
import type { VisaoGeral } from "../../profile-dashboard/types";
import { ToggleFollowCell } from "../../../components/datagrid/ToggleFollowCell";
import {
  BulkActionsBar,
  BulkAction,
} from "../../../components/datagrid/BulkActionsBar";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import PhonelinkOffIcon from "@mui/icons-material/PhonelinkOff";
import PostAddIcon from "@mui/icons-material/PostAdd";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import RadarIcon from "@mui/icons-material/Radar";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  FilterDrawer,
  ChipFilterConfig,
  SearchFilterConfig,
} from "../../../components/filter-drawer";
import { Container } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
// import StarBorderIcon from "@mui/icons-material/StarBorder";
// import StarIcon from "@mui/icons-material/Star";
import {
  ProfileDetailsDialog,
  type ReportType,
} from "../components/profile-details-modal";
import { usePermission } from "../../../hooks/use-permission";
import { USER_TYPES } from "../../../permissions";
import {
  RadarSyncDialog,
  type RadarSyncScope,
} from "../components/radar-sync-dialog";
import { FollowingProfilesService } from "../services";
import { CustomersService } from "../../clients/services";
import useUserStore from "../../../store/user";
import { toast } from "react-toastify";
import {
  AVISO_DUPLICATA,
  MOTIVO_COPIA,
  RECONFERIR_TOOLTIP,
  SELO_COPIA,
  travarSeCopia,
} from "./copia-no-google";
import { EditProfileModal } from "../../profiles-tracking/components/edit-profile-modal";
import { profilesTrackingService } from "../../profiles-tracking/services";
import { buildFollowingProfileUpdateData } from "./following-profile-save-payload";
import { buildProfileForModal } from "./following-profile-modal-data";
import { handleError } from "../../../utils/parseApiReturn";
import { RelatorioService } from "../../relatorio/services";
import { linkDeWinback, validadeDoLink } from "../../../utils/acesso-ao-relatorio";
import { KeywordSyncStatus } from "../components/keyword-sync-status";
import { PROFILE_POLL_INTERVAL_MS, shouldPollProfiles } from "./profile-polling";

export interface Profile {
  profileBusinessId: string;
  razaoSocial: string;
  locatidade: string;
  email: string;
  updatedAt: string;
  profileStatus: "ATENCAO" | "CRITICO" | "OK" | "null";
  rankedKeywords: number;
  totalKeywords: number;
  pendingKeywords: number;
  syncingKeywords: number;
  noResultKeywords: number;
  failedKeywords: number;
  syncing: boolean;
  businessProfileId?: number;
  suspendedAiReviewReplies?: boolean;
  allowAiCommentResponse: boolean;
  suspendedPosts?: boolean;
  allowPosts: boolean;
  tracking: boolean;
  isFavorite: boolean;
  customerStatus?: "disabled" | "success" | "warning";
  statusLabel?: "Ativo" | "Suspenso" | "Cópia";
  /** O Google aponta conflito de propriedade: a conta enxerga, mas não controla. */
  copiaNoGoogle?: boolean;
  /** Ficha oficial que tem uma duplicata marcada como cópia no Google. */
  duplicataNoGoogle?: boolean;
}

interface ProfileForModal {
  id: string;
  name: string;
  category: string;
  status: "active" | "pending" | "paused" | "preparing";
  stats: {
    views: number;
    clicks: number;
    ranking: number;
  };
  shortName?: string;
  openingDate?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  hours?: {
    monday?: string | string[];
    tuesday?: string | string[];
    wednesday?: string | string[];
    thursday?: string | string[];
    friday?: string | string[];
    saturday?: string | string[];
    sunday?: string | string[];
  };
}

const initialFilters = {
  profileStatus: "",
  ranked: "",
  localidade: "",
  copias: "",
};

export const FollowingProfiles = () => {
  const { user } = useUserStore();
  const { hasRole } = usePermission();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  // Faixa de indicadores (GET /admin/overview): total em acompanhamento e a saúde de cada um.
  const [visao, setVisao] = useState<VisaoGeral | null>(null);
  const tokenLogado = useUserStore((state) => state.user?.token);
  useEffect(() => {
    if (!tokenLogado) return;
    let ativo = true;
    adminOverviewService
      .getVisaoGeral(tokenLogado)
      .then((dados) => ativo && setVisao(dados))
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, [tokenLogado]);
  const contagemStatus = (label: string) =>
    visao?.perfisPorStatus?.find((f: { label: string; value: number }) => f.label === label)?.value;
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [profileForModal, setProfileForModal] =
    useState<ProfileForModal | null>(null);
  const [profileDetailsReady, setProfileDetailsReady] = useState(false);
  const [profilesOnSync, setProfilesOnSync] = useState<string[]>([]);
  const [radarSyncProfile, setRadarSyncProfile] = useState<Profile | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasLoadedProfiles = useRef(false);

  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSortRankedKeywords = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "asc" : prev === "desc" ? "asc" : null
    );
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  // Efeito para buscar dados sempre que a paginação, filtros ou busca mudarem
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      setLoading(!hasLoadedProfiles.current);
      try {
        const params = {
          token: user.token,
          page: pagination.page - 1,
          size: pagination.pageSize,
          query: searchTerm,
          profileStatus:
            filters.profileStatus === "" ? undefined : filters.profileStatus,
          ranking: filters.ranked === "" ? undefined : filters.ranked,
          localidade: filters.localidade ?? undefined,
          copias: filters.copias === "" ? undefined : filters.copias,
          ...(sortDirection && { sort: `${sortDirection}` }),
        };

        const response =
          await FollowingProfilesService.getFollowingProfiles(params);
        if (response && response.content) {
          const mappedProfiles: Profile[] = response.content.map(
            (p: Profile) => ({
              profileBusinessId: p.profileBusinessId,
              razaoSocial: p.razaoSocial,
              locatidade: p.locatidade,
              email: p.email,
              updatedAt: p.updatedAt
                ? new Date(p.updatedAt).toLocaleDateString("pt-BR")
                : "-",
              profileStatus: p.profileStatus ?? "null",
              rankedKeywords: p.rankedKeywords,
              totalKeywords: p.totalKeywords,
              pendingKeywords: p.pendingKeywords ?? 0,
              syncingKeywords: p.syncingKeywords ?? 0,
              noResultKeywords: p.noResultKeywords ?? 0,
              failedKeywords: p.failedKeywords ?? 0,
              syncing: p.syncing ?? false,
              businessProfileId: p.businessProfileId,
              allowAiCommentResponse: !p.suspendedAiReviewReplies,
              allowPosts: !p.suspendedPosts,
              tracking: p.tracking,
              isFavorite: false,
              copiaNoGoogle: p.copiaNoGoogle ?? false,
              duplicataNoGoogle: p.duplicataNoGoogle ?? false,
            }),
          );
          setProfiles(mappedProfiles);
          setPagination((prev) => ({
            ...prev,
            totalCount: response.totalElements,
          }));
        } else {
          setProfiles([]);
          setPagination((prev) => ({ ...prev, totalCount: 0 }));
        }
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
        toast.error("Falha ao carregar perfis. Tente novamente.");
        setProfiles([]);
      } finally {
        hasLoadedProfiles.current = true;
        setLoading(false);
      }
    };

    fetchData();
  }, [pagination.page, pagination.pageSize, filters, searchTerm, user?.token, sortDirection, refreshKey]);

  useEffect(() => {
    if (!shouldPollProfiles(profiles, document.visibilityState)) return;
    const interval = window.setInterval(() => {
      if (shouldPollProfiles(profiles, document.visibilityState)) {
        setRefreshKey((value) => value + 1);
      }
    }, PROFILE_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [profiles]);

  const handleEdit = useCallback(
    async (id: string) => {
      const profile = profiles.find((p) => p.profileBusinessId === id);
      if (profile) {
        setSelectedProfile(profile);
        setProfileForModal(null);
        setProfileDetailsReady(false);
        setDetailsOpen(false);
        setEditModalOpen(false);
        try {
          const completeDetails =
            await profilesTrackingService.getProfileBusinessDetails(
              profile.profileBusinessId.toString(),
            );
          setProfileForModal(buildProfileForModal(profile, completeDetails));
          setProfileDetailsReady(true);
          setEditModalOpen(true);
        } catch (error) {
          console.error("Erro ao carregar detalhes completos do perfil:", error);
          setSelectedProfile(null);
          toast.warn(
            "Nao foi possivel carregar os dados completos. A edicao nao foi aberta para evitar perda de informacoes.",
          );
        }
        return;
      }
    },
    [profiles],
  );

  const handleSaveProfile = useCallback(
    async (updatedProfile: ProfileForModal) => {
      console.log("FOLLOWING-PROFILES handleSaveProfile called");
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        return;
      }

      if (!selectedProfile) return;
      if (!profileDetailsReady || !profileForModal) {
        toast.warn("Aguarde o carregamento completo dos dados antes de salvar.");
        return;
      }

      try {
        await profilesTrackingService.updateProfile(
          selectedProfile.profileBusinessId,
          buildFollowingProfileUpdateData(profileForModal, updatedProfile),
        );

        // Atualizar a lista de perfis
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === selectedProfile.profileBusinessId
              ? {
                  ...p,
                  razaoSocial: updatedProfile.name,
                  locatidade: updatedProfile.city || "",
                  email: updatedProfile.email || "",
                }
              : p,
          ),
        );
        setEditModalOpen(false);
        setSelectedProfile(null);
        toast.success("Perfil atualizado com sucesso!");
      } catch (error) {
        console.error("Erro ao salvar perfil:", error);
        const { message } =
          error instanceof Error
            ? { message: error.message }
            : handleError(error, "Erro ao salvar alteracoes do perfil.");
        toast.error(message);
        throw new Error(message);
      }
    },
    [user?.token, selectedProfile, profileForModal, profileDetailsReady],
  );

  // const handleFavorite = (id: number) => {
  //   console.log(`Salvando status de favorito para o perfil ${id} no banco...`);
  //   setProfiles((prev) =>
  //     prev.map((p) => (p.profileBusinessId === id ? { ...p, isFavorite: !p.isFavorite } : p))
  //   );
  // };

  const handleNameClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setDetailsOpen(true);
  };

  // O painel de detalhe mostra o perfil selecionado; quando a lista muda
  // (toggle de automação, sincronização), o painel acompanha.
  useEffect(() => {
    if (!selectedProfile || !detailsOpen) return;
    const atual = profiles.find((p) => p.profileBusinessId === selectedProfile.profileBusinessId);
    if (atual && atual !== selectedProfile) setSelectedProfile(atual);
  }, [profiles, selectedProfile, detailsOpen]);

  const handleDisableFollow = async (id: string) => {
    await handleToggleFollow(id, false);
    setDetailsOpen(false);
  };

  const handleToggleFollow = useCallback(async (id: string, checked: boolean) => {
    if (!user?.token) return;

    // Otimistic update
    setProfiles((prev) =>
      prev.map((p) =>
        p.profileBusinessId === id ? { ...p, tracking: checked } : p,
      ),
    );

    try {
      await FollowingProfilesService.toggleTracking(Number(id), user.token);
    } catch {
      // Rollback
      setProfiles((prev) =>
        prev.map((p) =>
          p.profileBusinessId === id ? { ...p, tracking: !checked } : p,
        ),
      );
    }
  }, [user?.token]);

  const handleSyncKeywords = useCallback(
    async (
      profileId: string,
      client: "radar" | "serp_api",
      scope?: RadarSyncScope,
    ) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.syncKeywords({
          businessProfileId: profileId,
          client,
          scope,
          token: user?.token || "",
        });
        setRefreshKey((value) => value + 1);
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  // "Reconferir no Google" de uma ficha marcada como cópia: o cliente resolveu o
  // conflito e o operador não quer esperar a rodada diária do backend.
  const handleReconferirCopia = useCallback(
    async (profileId: string) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        const resposta = await FollowingProfilesService.reconferirCopia(
          profileId,
          user?.token || "",
        );
        if (resposta?.copia === false) {
          toast.success(resposta.mensagem);
        } else {
          toast.info(resposta?.mensagem ?? "O Google ainda aponta conflito nesta ficha.");
        }
        setRefreshKey((value) => value + 1);
      } catch (erro) {
        console.error(erro);
        toast.error("Não foi possível consultar o Google agora. Tente de novo em alguns minutos.");
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSwitchAiCommentResponse = useCallback(
    async (profileId: string, allowAiCommentResponse: boolean) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.switchAiCommentResponse({
          businessProfileId: profileId,
          allowAiCommentResponse,
          token: user?.token || "",
        });
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === profileId
              ? { ...p, allowAiCommentResponse: !p.allowAiCommentResponse }
              : p,
          ),
        );
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSwitchPosts = useCallback(
    async (profileId: string, currentlyAllowed: boolean) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.switchPostsSuspension({
          businessProfileId: profileId,
          suspendPosts: currentlyAllowed,
          token: user?.token || "",
        });
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === profileId
              ? { ...p, allowPosts: !p.allowPosts }
              : p,
          ),
        );
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSelectRow = (rowId: string | number) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? profiles.map((p) => p.profileBusinessId) : []);
  };

  const handleBulkToggleFollow = () => {
    setProfiles((prev) =>
      prev.map((p) =>
        selectedRowIds.includes(p.profileBusinessId)
          ? { ...p, following: !p.tracking }
          : p,
      ),
    );
    setSelectedRowIds([]);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    // Aqui você chamaria a API para buscar os dados filtrados
    console.log(`Filtering by ${field}: ${value}`);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    // Aqui você chamaria a API para buscar os dados sem filtros
    console.log("Clearing filters");
  };

  const handleSearchLocation = (query: string) => {
    // Aqui você chamaria a API para buscar por localidade
    console.log("Searching location in DB:", query);
  };

  const chipFilters: ChipFilterConfig[] = [
    {
      id: "profileStatus",
      label: "Status",
      options: [
        { label: "Todos", value: "" },
        { label: "Ok", value: "OK" },
        { label: "Atenção", value: "ATENCAO" },
        { label: "Crítico", value: "CRITICO" },
      ],
    },
    {
      id: "ranked",
      label: "Palavras Ranqueadas",
      options: [
        { label: "Todas", value: "" },
        { label: "Zero", value: "zero" },
        { label: "1-7", value: "1-7" },
        { label: "8+", value: "8+" },
      ],
    },
    {
      // Fichas em que o Google aponta conflito de propriedade. Sem este filtro elas só
      // aparecem quando se busca pelo nome (ver filtroDeCopia no backend).
      id: "copias",
      label: "Duplicata no Google",
      options: [
        { label: "Todos", value: "" },
        { label: "Só duplicatas", value: "somente" },
      ],
    },
  ];

  const searchFilters: SearchFilterConfig[] = [
    {
      id: "location",
      label: "Localidade",
      placeholder: "Buscar por localidade",
      onSearch: handleSearchLocation,
    },
  ];

  const columns: DataGridColumn[] = [
    { id: "name", header: "Perfil", minWidth: 260 },
    { id: "status", header: "Status" },
    {
      id: "keywords",
      mobileLabel: "Palavras",
      header: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Palavras
          <Tooltip
            title={
              sortDirection === null
                ? "Ordenar decrescente"
                : sortDirection === "desc"
                ? "Ordenar crescente"
                : "Remover ordenação"
            }
          >
            <IconButton size="small" onClick={handleSortRankedKeywords}>
              {sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : sortDirection === "asc" ? (
                <ArrowUpwardIcon fontSize="inherit" />
              ) : (
                <UnfoldMoreIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
      minWidth: 150,
    },
    { id: "sync", header: "Sincronização do ranking", minWidth: 220, hideOnMobile: true },
    { id: "automation", header: "Automação", minWidth: 150 },
    { id: "actions", header: "Ações", align: "right", minWidth: 150 },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    return profiles.map((profile) => {
      const canSyncProfile =
          typeof profile.profileBusinessId === "string" &&
          profile.profileBusinessId.length > 0;
        const businessProfileId = profile.profileBusinessId ?? "";
      const syncDisabled =
        !canSyncProfile || profilesOnSync.includes(businessProfileId);
      const copia = profile.copiaNoGoogle === true;

        // Ações com os mesmos handlers, tooltips e travas de antes (travarSeCopia).
        const acoes = {
          editar: travarSeCopia(
            {
              icon: null,
              disabled: false,
              tooltip: "Editar",
              onClick: () => handleEdit(profile.profileBusinessId),
            },
            copia,
          ),
          posts: travarSeCopia(
            {
              icon: null,
              disabled: syncDisabled,
              tooltip: profile.allowPosts
                ? "Postagens automáticas ATIVAS — clique para pausar"
                : "Postagens automáticas PAUSADAS — clique para reativar",
              onClick: () => handleSwitchPosts(businessProfileId, profile.allowPosts),
            },
            copia,
          ),
          ia: travarSeCopia(
            {
              icon: null,
              disabled: syncDisabled,
              tooltip: profile.allowAiCommentResponse
                ? "Respostas de IA nas avaliações ATIVAS — clique para pausar"
                : "Respostas de IA nas avaliações PAUSADAS — clique para reativar",
              onClick: () =>
                handleSwitchAiCommentResponse(businessProfileId, profile.allowAiCommentResponse),
            },
            copia,
          ),
          radar: travarSeCopia(
            {
              icon: null,
              disabled: syncDisabled,
              tooltip: "Sincronizar palavras-chave via Radar",
              onClick: () => setRadarSyncProfile(profile),
            },
            copia,
          ),
          serp: travarSeCopia(
            {
              icon: null,
              disabled: syncDisabled,
              tooltip: "Sincronizar palavras-chave via SerpAPI",
              onClick: () => handleSyncKeywords(businessProfileId, "serp_api"),
            },
            copia,
          ),
        };
        const tone =
          copia ? "grey"
          : profile.profileStatus === "OK" ? "green"
          : profile.profileStatus === "ATENCAO" ? "yellow"
          : profile.profileStatus === "CRITICO" ? "red"
          : "grey";
        const subtitulo = [profile.locatidade, profile.email].filter(Boolean).join(" · ");

        return {
        id: profile.profileBusinessId,
        render: {
          name: (
            <Box>
              <NameCell
                primaryText={profile.razaoSocial ?? "-"}
                secondaryText={subtitulo || undefined}
                avatar={iniciais(profile.razaoSocial)}
                avatarTone={tone}
                onClick={() => handleNameClick(profile)}
              />
              {profile.duplicataNoGoogle && (
                <Tooltip title={AVISO_DUPLICATA} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={{ color: "#B06000", cursor: "help", display: "block", mt: 0.5, ml: "52px" }}
                  >
                    ⚠ Tem duplicata no Google
                  </Typography>
                </Tooltip>
              )}
            </Box>
          ),
          status: copia ? (
            <Tooltip title={MOTIVO_COPIA} arrow placement="top">
              <span>
                <StatusChip status="warning" label={SELO_COPIA} />
              </span>
            </Tooltip>
          ) : (
            <StatusChip status={profile.profileStatus} />
          ),
          keywords: (
            <KeywordBarCell ranked={profile.rankedKeywords ?? 0} total={profile.totalKeywords} />
          ),
          sync: (
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 124 }}>
                <KeywordSyncStatus
                  pendingKeywords={profile.pendingKeywords}
                  syncingKeywords={profile.syncingKeywords}
                  noResultKeywords={profile.noResultKeywords}
                  failedKeywords={profile.failedKeywords}
                  totalKeywords={profile.totalKeywords}
                />
                <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                  {profile.updatedAt}
                </Typography>
              </Box>
              <Box sx={{ display: "inline-flex", gap: "2px" }}>
                <Tooltip title={acoes.radar.tooltip} arrow>
                  <span>
                    <IconButton size="small" disabled={acoes.radar.disabled} onClick={acoes.radar.onClick} aria-label={acoes.radar.tooltip}>
                      <RadarIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={acoes.serp.tooltip} arrow>
                  <span>
                    <IconButton size="small" disabled={acoes.serp.disabled} onClick={acoes.serp.onClick} aria-label={acoes.serp.tooltip}>
                      <TravelExploreIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ),
          automation: (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <ToggleFollowCell
                checked={profile.tracking}
                onToggle={(checked) => handleToggleFollow(profile.profileBusinessId, checked)}
              />
              <Tooltip title={acoes.posts.tooltip} arrow>
                <span>
                  <AutomationToggle
                    type="button"
                    $on={!!profile.allowPosts}
                    disabled={acoes.posts.disabled}
                    onClick={acoes.posts.onClick}
                    aria-label={acoes.posts.tooltip}
                    aria-pressed={!!profile.allowPosts}
                  >
                    <PostAddIcon fontSize="small" />
                  </AutomationToggle>
                </span>
              </Tooltip>
              <Tooltip title={acoes.ia.tooltip} arrow>
                <span>
                  <AutomationToggle
                    type="button"
                    $on={!!profile.allowAiCommentResponse}
                    disabled={acoes.ia.disabled}
                    onClick={acoes.ia.onClick}
                    aria-label={acoes.ia.tooltip}
                    aria-pressed={!!profile.allowAiCommentResponse}
                  >
                    <PhonelinkOffIcon fontSize="small" />
                  </AutomationToggle>
                </span>
              </Tooltip>
            </Box>
          ),
          actions: (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              {copia && (
                <Tooltip title={RECONFERIR_TOOLTIP} arrow>
                  <span>
                    <IconButton size="small" disabled={syncDisabled} onClick={() => handleReconferirCopia(businessProfileId)} aria-label={RECONFERIR_TOOLTIP}>
                      <CachedOutlinedIcon fontSize="small" color="secondary" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={acoes.editar.tooltip} arrow>
                <span>
                  <TonalButton type="button" disabled={acoes.editar.disabled} onClick={acoes.editar.onClick}>
                    <ModeEditOutlineIcon fontSize="small" />
                    Gerenciar
                  </TonalButton>
                </span>
              </Tooltip>
              <Tooltip title="Ver detalhes e relatórios" arrow>
                <IconButton size="small" onClick={() => handleNameClick(profile)} aria-label="Ver detalhes e relatórios">
                  <BarChartOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
      };
    });
  }, [
    profiles,
    profilesOnSync,
    handleEdit,
    handleSwitchAiCommentResponse,
    handleSwitchPosts,
    handleSyncKeywords,
    handleToggleFollow,
    handleReconferirCopia,
  ]);

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filters.profileStatus && filters.profileStatus !== "") {
      chips.push({
        id: "profileStatus",
        label: `Status: ${filters.profileStatus}`,
        onDelete: () => handleFilterChange("profileStatus", ""),
      });
    }
    if (filters.ranked && filters.ranked !== "") {
      chips.push({
        id: "ranked",
        label: `Ranqueadas: ${filters.ranked}`,
        onDelete: () => handleFilterChange("ranked", ""),
      });
    }
    if (filters.localidade) {
      chips.push({
        id: "location",
        label: `Local: ${filters.localidade}`,
        onDelete: () => handleFilterChange("location", ""),
      });
    }
    if (filters.copias) {
      chips.push({
        id: "copias",
        label: "Só duplicatas no Google",
        onDelete: () => handleFilterChange("copias", ""),
      });
    }
    return chips;
  }, [filters]);

  const handleViewDashboard = (profileId: string) => {
    window.open(`/dashboard/${profileId}`, "_blank");
  };

  const handleOpenReport = (profileId: string, tipo: ReportType) => {
    window.open(
      tipo === "winback"
        ? `/relatorio-winback/${profileId}`
        : `/relatorio/${profileId}`,
      "_blank"
    );
  };

  // Relatório só abre com login; para o ex-cliente a equipe manda este link,
  // que abre o relatório winback sem login por 24h.
  const handleCopyWinbackLink = async (profileId: string) => {
    const link = await RelatorioService.criarLinkDeWinback(profileId);
    if (!link) {
      toast.error("Não foi possível gerar o link de winback.");
      return;
    }
    const url = linkDeWinback(window.location.origin, profileId, link.token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link de winback copiado. Vale até ${validadeDoLink(link.expiresAt)}.`);
    } catch {
      window.prompt("Copie o link de winback (vale 24h):", url);
    }
  };

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Monitoramento de perfis"
          subtitle="Monitore e gerencie perfis com problemas de ranqueamento"
        />
        <Box sx={{ width: "calc(100% - 64px)", mt: 3 }}>
          <KpiStrip
            aria-label="Resumo dos perfis em acompanhamento"
            items={[
              { icon: "visibility", value: formatarNumero(visao?.perfisEmAcompanhamento), label: "em acompanhamento", tone: "brand" },
              { icon: "check_circle", value: formatarNumero(contagemStatus("OK")), label: "saudáveis", tone: "green" },
              { icon: "warning", value: formatarNumero(contagemStatus("ATENCAO")), label: "em atenção", tone: "yellow" },
              { icon: "error", value: formatarNumero(contagemStatus("CRITICO")), label: "críticos", tone: "red" },
            ]}
          />
        </Box>
        <Box
          sx={{ width: "calc(100% - 64px)", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            toolbarChips={
              <>
                <ToolbarDivider />
                <ChipGroupLabel>Status</ChipGroupLabel>
                {[
                  { label: "Todos", value: "", dot: null },
                  { label: "Ok", value: "OK", dot: "#1E8E3E" },
                  { label: "Atenção", value: "ATENCAO", dot: "#F9AB00" },
                  { label: "Crítico", value: "CRITICO", dot: "#D93025" },
                ].map((opcao) => (
                  <ChipButton
                    key={opcao.value}
                    type="button"
                    $on={(filters.profileStatus ?? "") === opcao.value}
                    aria-pressed={(filters.profileStatus ?? "") === opcao.value}
                    onClick={() => handleFilterChange("profileStatus", opcao.value)}
                  >
                    {opcao.dot && <span className="dot" style={{ background: opcao.dot }} />}
                    {opcao.label}
                  </ChipButton>
                ))}
              </>
            }
            resultsLabel={`${formatarNumero(pagination.totalCount)}${
              visao?.perfisEmAcompanhamento ? ` de ${formatarNumero(visao.perfisEmAcompanhamento)}` : ""
            } perfis`}
            columns={columns}
            rows={rows}
            tableLabel="perfis"
            selectedRowIds={selectedRowIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por nome, localidade ou email..." // parâmetro
            filterChips={filterChips} // chips ativos
            filterCount={filterChips.length} // contador no botão
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            bulkActionsBarRenderer={(selectedIds) => {
              const bulkActions: BulkAction[] = [
                {
                  label: "Alternar Acompanhamento",
                  onClick: handleBulkToggleFollow,
                },
                {
                  label: "Cancelar",
                  onClick: () => setSelectedRowIds([]),
                  color: "secondary",
                },
              ];

              return (
                <BulkActionsBar
                  selectedCount={selectedIds.length}
                  tableLabel="perfil"
                  actions={bulkActions}
                />
              );
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum perfil em acompanhamento"
            noDataDescription="Adicione perfis para começar o monitoramento"
            isLoading={loading}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
          searchFilters={searchFilters}
        />
        <ProfileDetailsDialog
          open={detailsOpen}
          profile={
            selectedProfile
              ? {
                  id: String(selectedProfile.profileBusinessId),
                  name: selectedProfile.razaoSocial,
                  location: selectedProfile.locatidade,
                  email: selectedProfile.email,
                  totalKw: selectedProfile.totalKeywords,
                  rankedValue: selectedProfile.rankedKeywords,
                  status: selectedProfile.customerStatus || "success",
                  statusLabel: selectedProfile.statusLabel || "Ativo",
                  lastUpdate: selectedProfile.updatedAt,
                  following: selectedProfile.tracking,
                  allowPosts: selectedProfile.allowPosts,
                  allowAiCommentResponse: selectedProfile.allowAiCommentResponse,
                  pendingKeywords: selectedProfile.pendingKeywords,
                  syncingKeywords: selectedProfile.syncingKeywords,
                  noResultKeywords: selectedProfile.noResultKeywords,
                  failedKeywords: selectedProfile.failedKeywords,
                  copia: selectedProfile.copiaNoGoogle === true,
                }
              : null
          }
          busy={selectedProfile ? profilesOnSync.includes(selectedProfile.profileBusinessId) : false}
          onSyncRadar={() => selectedProfile && setRadarSyncProfile(selectedProfile)}
          onSyncSerp={(id) => handleSyncKeywords(id, "serp_api")}
          onTogglePosts={(id, allowed) => handleSwitchPosts(id, allowed)}
          onToggleAi={(id, allowed) => handleSwitchAiCommentResponse(id, allowed)}
          onClose={() => setDetailsOpen(false)}
          onEdit={(id: string | number) => handleEdit(String(id))}
          onViewDetails={(id: string) => {
            handleViewDashboard(id);
          }}
          onDisableFollow={(id: string | number) =>
            handleDisableFollow(String(id))
          }
          showDisableFollow
          showEditAction
          onOpenReport={handleOpenReport}
          onCopyWinbackLink={handleCopyWinbackLink}
          showReportActions={hasRole(USER_TYPES.ADMIN)}
        />
        <EditProfileModal
          isOpen={editModalOpen}
          profile={profileForModal}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedProfile(null);
            setProfileForModal(null);
          }}
          onSave={handleSaveProfile}
        />
        <RadarSyncDialog
          open={Boolean(radarSyncProfile)}
          profileName={radarSyncProfile?.razaoSocial ?? "este perfil"}
          loading={Boolean(
            radarSyncProfile &&
              profilesOnSync.includes(radarSyncProfile.profileBusinessId),
          )}
          onClose={() => setRadarSyncProfile(null)}
          onConfirm={async (scope) => {
            if (!radarSyncProfile) return;
            await handleSyncKeywords(
              radarSyncProfile.profileBusinessId,
              "radar",
              scope,
            );
            setRadarSyncProfile(null);
          }}
        />
      </Container>
    </Page.Root>
  );
};
